/* eslint-disable function-paren-newline */
/* eslint-disable indent */
/* eslint-disable no-confusing-arrow */
/* eslint-disable implicit-arrow-linebreak */
import { chain, isNil, merge, omitBy, pickBy, sortBy } from 'lodash';
import { FormattedMessage } from 'react-intl';

export const getCustomFieldValue = (name, data, defaultValue = '') => {
  return data?.customFields
    ? data?.customFields[name.replace('customFields.', '')]
    : defaultValue;
};

/**
 * Resolves dynamic date values like "today" to actual dates
 * @param {string} value - The date value to resolve
 * @returns {string} - Formatted date string or original value
 */
export const resolveDateValue = (value) => {
  if (value === 'today') {
    const today = new Date();
    return today.toISOString().split('T')[0].replace(/-/g, '/');
  }
  return value;
};

const englishOnlyRegex = new RegExp('^[a-zA-Z_]+$');

export const validateEnglishOnly = (value) =>
  !value || englishOnlyRegex.test(value) ? undefined : (
    <strong>
      <FormattedMessage
        id="ui-plugin-inventory-templates.validation.englishOnly"
        defaultMessage="Only english characters allowed!"
      />
    </strong>
  );

export const composeValidators =
  (...validators) =>
  (value) =>
    validators
      .filter(validator => typeof validator === 'function')
      .reduce(
        (error, validator) => error || validator(value),
        undefined,
      );

export const generateBarcodeValue = (
  permanentLocationId,
  locationCode,
  barcodeLastIndex,
) => {
  if (permanentLocationId) {
    const generatedValue =
      locationCode?.split('/').slice(2).join('.').replace(/\//g, '.') +
      new Date().getFullYear().toString().replace('20', '') +
      '0' +
      (new Date().getMonth() + 1) +
      '000' +
      (barcodeLastIndex + 1);
    return generatedValue;
  } else {
    return '';
  }
};

export const parseInstanceRelationship = (fieldArray, titleKey) => {
  if (fieldArray?.length) {
    return fieldArray.map((field) => ({
      [titleKey]: field.id,
      instanceRelationshipTypeId: field.instanceRelationshipTypeId,
    }));
  }

  return fieldArray;
};

// export const parseInstanceParentRelationship = (fieldArray) => {
//   if (fieldArray?.length) {
//     return fieldArray.map((field) => ({
//       superInstanceId: field.id,
//       instanceRelationshipTypeId: field.instanceRelationshipTypeId
//     }));
//   }

//   return fieldArray;
// };

export const parseClassifications = (fieldArray) => {
  if (fieldArray?.length) {
    return fieldArray.map((field) => ({
      classificationTypeId: field.classificationTypeId,
      classificationNumber: `${field.classificationNumber} ${field.authorNumber || ''}${field.editionNumber ? ` ${field.editionNumber}` : ''}`,
    }));
  }

  return fieldArray;
};

export const UnParseClassifications = (fieldArray) => {
  if (fieldArray?.length) {
    return fieldArray.map((field) => {
      const formattedClassificationNumber =
        field.classificationNumber.split(' ');
      const length = field.classificationNumber.length;
      const count = field.classificationNumber.split(' ').length - 1;
      const firstIndex = field.classificationNumber.indexOf(' ');
      const lastIndex = field.classificationNumber.lastIndexOf(' ');
      // const editionNumber = field.classificationNumber.substring(lastIndexOf);
      const classificationNumber =
        firstIndex !== -1
          ? field.classificationNumber.slice(0, firstIndex)
          : field.classificationNumber;
      const authorNumberLastIndex =
        count + lastIndex !== length ? lastIndex : lastIndex + count;
      const authorNumber =
        firstIndex !== -1
          ? field.classificationNumber.slice(
              firstIndex + 1,
              authorNumberLastIndex + 2,
            )
          : '';
      const editionNumber =
        firstIndex !== -1 && count + lastIndex !== length
          ? field.classificationNumber.slice(lastIndex + 2)
          : '';

      if (formattedClassificationNumber.length) {
        return {
          classificationTypeId: field.classificationTypeId,
          classificationNumber,
          authorNumber,
          editionNumber,
        };
      }

      return {
        classificationTypeId: field.classificationTypeId,
        classificationNumber: field.classificationNumber,
        authorNumber: null,
        editionNumber: null,
      };
    });
  }

  return fieldArray;
};

const formatDefaultValueByType = (obj) => {
  if (obj?.isRepeatable) {
    return {
      [obj?.name]: [],
    };
  } else if (obj.type === 'checkbox') {
    return {
      [obj?.name]: JSON.parse(obj?.defaultValue),
    };
  } else if (obj.type === 'date' && obj?.defaultValue === 'today') {
    // Convert "today" to current date in YYYY/MM/DD format
    return {
      [obj?.name]: resolveDateValue(obj?.defaultValue),
    };
  } else {
    return {
      [obj?.name]: obj?.defaultValue,
    };
  }
};

const getDefaultValuesObject = (obj) => {
  const objName = obj.name.split('.');
  if (obj.defaultValue && obj.defaultValue !== '') {
    if (objName.length > 1) {
      // Handle nested fields with date resolution
      const resolvedValue =
        obj.type === 'date' && obj?.defaultValue === 'today'
          ? resolveDateValue(obj?.defaultValue)
          : obj?.defaultValue;

      return {
        [objName[0]]: {
          [objName[1]]: !obj?.isRepeatable ? resolvedValue : [],
        },
      };
    } else {
      return formatDefaultValueByType(obj);
    }
  }
  return {};
};

export const setDefaultValues = (entityValues) => {
  const templateValues = chain(entityValues)
    .map('sectionFields')
    .flatten()
    .value();

  return templateValues.reduce((acc, field) => {
    return merge(acc, getDefaultValuesObject(field));
  }, {});
};

export const setInstancePayload = (values, instanceId, instance = {}) => {
  const { instanceData } = values;
  instanceData.id = instanceId;

  /** save classifications data into custom fields to make sure classification number
   * formatted correctly (classificationNumber, kater, edition)
   */
  instanceData.customFields = {
    ...instanceData.customFields,
    classifications: instanceData.classifications,
  };

  instanceData.childInstances =
    parseInstanceRelationship(instanceData.childInstances, 'subInstanceId') ||
    [];
  instanceData.parentInstances =
    parseInstanceRelationship(
      instanceData.parentInstances,
      'superInstanceId',
    ) || [];

  instanceData.source = 'FOLIO';

  return instanceData;
};

export const setHoldingPostPayload = (holdingData, values) => {
  const {
    holdingDiscoverySuppress,
    holdingCopyNumber,
    holdingAdministrativeNotes,
    holdingStatisticalCodeIds,
    holdingElectronicAccess,
    holdingsNotes,
    holdingHrid,
    holdingsItems,
    bareHoldingsItems,
    ...rest
  } = holdingData;

  const payload = {
    ...rest,
    ...{
      copyNumber: holdingCopyNumber,
      discoverySuppress: holdingDiscoverySuppress,
      administrativeNotes: holdingAdministrativeNotes,
      statisticalCodeIds: holdingStatisticalCodeIds,
      instanceId: values.instanceData.id,
      sourceId: values.holdingSourceId,
      notes: holdingsNotes,
      electronicAccess: holdingElectronicAccess,
    },
  };

  return payload;
};

// edit instance helper functions
export const setHoldingPutPayload = (holdingFormValues, holding = {}) => {
  const {
    holdingDiscoverySuppress,
    holdingCopyNumber,
    holdingAdministrativeNotes,
    holdingStatisticalCodeIds,
    holdingsNotes,
    holdingElectronicAccess,
    holdingHrid,
    holdingsItems,
    bareHoldingsItems,
    ...rest
  } = holdingFormValues;

  const payload = {
    ...rest,
    ...{
      copyNumber: holdingCopyNumber,
      discoverySuppress: holdingDiscoverySuppress,
      administrativeNotes: holdingAdministrativeNotes,
      statisticalCodeIds: holdingStatisticalCodeIds,
      notes: holdingsNotes,
      electronicAccess: holdingElectronicAccess,
      hrid: holdingHrid,
    },
  };

  return payload;
};

export const setItemPostPayload = (itemData) => {
  const {
    itemDiscoverySuppress,
    itemAdministrativeNotes,
    itemStatisticalCodeIds,
    itemElectronicAccess,
    itemNotes,
    itemHrid,
    itemFormerIds,
    barcode,
    ...rest
  } = itemData;

  const payload = {
    ...rest,
    ...{
      discoverySuppress: itemDiscoverySuppress,
      administrativeNotes: itemAdministrativeNotes,
      statisticalCodeIds: itemStatisticalCodeIds,
      notes: itemNotes,
      electronicAccess: itemElectronicAccess,
      formerIds: itemFormerIds,
    },
  };

  // Add barcode only if it has a value
  if (barcode) {
    payload.barcode = barcode;
  }

  return payload;
};

export const setItemPutPayload = (itemData, item = {}) => {
  const {
    itemDiscoverySuppress,
    itemAdministrativeNotes,
    itemStatisticalCodeIds,
    itemElectronicAccess,
    itemNotes,
    itemFormerIds,
    itemHrid,
    barcode,
    ...rest
  } = itemData;

  const payload = {
    ...rest,
    ...{
      hrid: itemHrid,
      discoverySuppress: itemDiscoverySuppress,
      administrativeNotes: itemAdministrativeNotes,
      statisticalCodeIds: itemStatisticalCodeIds,
      notes: itemNotes,
      electronicAccess: itemElectronicAccess,
      formerIds: itemFormerIds,
    },
  };

  // Add barcode only if it has a value
  if (barcode) {
    payload.barcode = barcode;
  }

  console.log({ itemData, payload });

  return payload;
};

export const unParseHoldingsData = (holdings) => {
  return sortBy(
    holdings?.map((holding) => {
      const {
        discoverySuppress,
        copyNumber,
        administrativeNotes,
        statisticalCodeIds,
        notes,
        electronicAccess,
        hrid,
        ...rest
      } = holding;
      return {
        ...rest,
        holdingDiscoverySuppress: discoverySuppress,
        holdingCopyNumber: copyNumber,
        holdingAdministrativeNotes: administrativeNotes,
        holdingStatisticalCodeIds: statisticalCodeIds,
        holdingsNotes: notes,
        holdingElectronicAccess: electronicAccess,
        holdingHrid: hrid,
      };
    }),
    ['holdingCopyNumber'],
  );
};

export const unParseItemsData = (items) => {
  const parsedItems = {};
  for (const key in items) {
    if (Object.hasOwn(items, key)) {
      const itemsArr = items[key]?.map((item) => {
        const {
          discoverySuppress,
          copyNumber,
          administrativeNotes,
          statisticalCodeIds,
          notes,
          electronicAccess,
          hrid,
          ...rest
        } = item;
        return {
          ...rest,
          itemDiscoverySuppress: discoverySuppress,
          copyNumber,
          itemAdministrativeNotes: administrativeNotes,
          itemStatisticalCodeIds: statisticalCodeIds,
          itemsNotes: notes,
          itemElectronicAccess: electronicAccess,
          itemHrid: hrid,
        };
      });
      const sortedItemsArr = sortBy(itemsArr, (item) =>
        Number(item.copyNumber),
      );
      parsedItems[key] = sortedItemsArr;
    }
  }
  return parsedItems;
};

export const getHoldingsStatus = (formValues, holdings) => {
  const formHoldingsData = formValues.holdingsData || [];
  const deletedHoldings = holdings.filter((holding) => {
    return !formHoldingsData.some((formHolding) => {
      return holding.id === formHolding.id;
    });
  });

  const newHoldings = formHoldingsData.filter((formHolding) => {
    return !holdings.some((holding) => {
      return formHolding.id === holding.id;
    });
  });

  const updatedHoldings = formHoldingsData.filter((formHolding) => {
    return holdings.some((holding) => {
      return formHolding.id === holding.id;
    });
  });

  return { deletedHoldings, newHoldings, updatedHoldings };
};

export const getItemsStatus = (formValues, items) => {
  // const originItemsData = formValues.holsData || [];

  const deletedItems = items.filter((item) => {
    return !formValues.some((formItem) => {
      return item.id === formItem.id;
    });
  });

  const newItems = formValues.filter((formItem) => {
    return !items.some((item) => {
      return formItem.id === item.id;
    });
  });

  const updatedItems = formValues.filter((formItem) => {
    return items.some((item) => {
      return formItem.id === item.id;
    });
  });

  return { deletedItems, newItems, updatedItems };
};

export const required = (value) =>
  value ? undefined : (
    <strong>
      <FormattedMessage
        id="ui-reports.validation.required"
        defaultMessage="required"
      />
    </strong>
  );

export const requiredFieldArray = (values) => {
  return values?.length ? undefined : (
    <strong>
      <FormattedMessage
        id="ui-reports.validation.required"
        defaultMessage="required"
      />
    </strong>
  );
};

// Authority validation functions for React Final Form
export const authorityMinLength = (value) => {
  return !value || value.length >= 3 ? undefined : (
    <FormattedMessage
      id="ui-plugin-inventory-templates.authority.validation.minLength"
      defaultMessage="Search term must be at least 3 characters"
    />
  );
};

// Convert Hindu-Arabic numerals (٠١٢٣٤٥٦٧٨٩) to Latin numerals (0123456789)
export const convertHinduArabicToLatin = (text) => {
  if (!text) return text;

  const hinduArabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const latinDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  let convertedText = text;
  hinduArabicDigits.forEach((hinduDigit, index) => {
    const regex = new RegExp(hinduDigit, 'g');
    convertedText = convertedText.replace(regex, latinDigits[index]);
  });

  return convertedText;
};

export const authorityTextOnly = (value) => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  if (value.trim().length === 0) return undefined;

  const trimmedValue = value.trim();

  // Convert Hindu-Arabic numerals to Latin for consistent validation
  const normalizedValue = convertHinduArabicToLatin(trimmedValue);

  // Check if input is purely numeric (with optional decimal) after normalization
  if (/^\d+(\.\d+)?$/.test(normalizedValue)) {
    return (
      <FormattedMessage
        id="ui-plugin-inventory-templates.authority.validation.textOnly"
        defaultMessage="Search term must be text, not numeric"
      />
    );
  }

  // Allow all texts containing characters (Arabic, English, symbols, etc.)
  // Only prevent texts containing dangerous or unwanted symbols
  const dangerousChars = /[<>{}|\\^~`]/;
  if (dangerousChars.test(trimmedValue)) {
    return (
      <FormattedMessage
        id="ui-plugin-inventory-templates.text.validation.invalidCharacters"
        defaultMessage="Text contains invalid characters"
      />
    );
  }

  return undefined;
};

export const createAuthorityMustLinkValidator =
  (isAuthorityField, hasAuthority, suggestions, isLoading) => (value) => {
    if (
      !isAuthorityField ||
      !value ||
      value.length < 3 ||
      hasAuthority ||
      isLoading
    ) {
      return undefined;
    }

    // Only show warning if search completed and no results
    if (suggestions && suggestions.length === 0) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.authority.validation.mustLink"
          defaultMessage="This term should be linked to an authority record"
        />
      );
    }

    return undefined;
  };

// Combined authority validation factory
export const createAuthorityValidator =
  (isAuthorityField, hasAuthority, suggestions, isLoading) => (value) => {
    if (!isAuthorityField) return undefined;

    const minLengthError = authorityMinLength(value);
    if (minLengthError) return minLengthError;

    const textOnlyError = authorityTextOnly(value);
    if (textOnlyError) return textOnlyError;

    const mustLinkError = createAuthorityMustLinkValidator(
      isAuthorityField,
      hasAuthority,
      suggestions,
      isLoading,
    )(value);
    if (mustLinkError) return mustLinkError;

    return undefined;
  };

// Error types for optimistic locking
export const ERROR_TYPES = {
  OPTIMISTIC_LOCKING: 'optimisticLocking',
};

/**
 * Parses http error to json and attaches an error type.
 * Based on ui-inventory parseHttpError utility.
 *
 * @param httpError object
 * @returns object
 */
export const parseHttpError = async (httpError) => {
  const contentType = httpError?.headers?.get('content-type');
  let jsonError = {};

  try {
    if (contentType === 'text/plain') {
      jsonError.message = await httpError.text();
    } else {
      jsonError = await httpError.json();
    }

    // Optimistic locking error is currently returned as a plain text
    // Check for optimistic locking patterns in error message
    if (
      jsonError.message?.match(/optimistic locking/i) ||
      jsonError.message?.match(/has been changed.*version/i)
    ) {
      jsonError.errorType = ERROR_TYPES.OPTIMISTIC_LOCKING;
    }

    return jsonError;
  } catch (err) {
    return httpError;
  }
};
